<template>
  <v-sheet class="register-view-container">
    <!-- Loading Overlay - visible during data fetching and pagination -->
    <v-overlay
      :value="isLoading"
      absolute
      color="white"
      opacity="0.7"
      z-index="5"
      class="register-loading-overlay">
      <div class="d-flex flex-column align-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
          width="4">
        </v-progress-circular>
        <span class="mt-3 text-subtitle-2 font-weight-medium primary--text">
          Loading {{ currentRegisterType }} Register...
        </span>
      </div>
    </v-overlay>

    <!-- Table Section -->
    <v-data-table
      class="register-table elevation-0 d-flex flex-column table-sticky custom-table flex-grow-1"
      ref="table"
      :options.sync="options"
      :headers="visibleHeaders"
      :items="entries"
      :items-per-page="options.itemsPerPage"
      :server-items-length="total"
      :loading="isLoading"
      :item-class="getRowClass"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-chevron-double-left',
        lastIcon: 'mdi-chevron-double-right',
        prevIcon: 'mdi-chevron-left',
        nextIcon: 'mdi-chevron-right',
        itemsPerPageText: 'Row/Page',
        itemsPerPageOptions: [15, 30, 50, 100],
      }">
      <!-- Enhanced Table Toolbar -->
      <template v-slot:top>
        <div class="table-toolbar-enhanced">
          <!-- Left Section: Column Controls + Selection -->
          <div class="toolbar-left">
            <columns-visibility-control
              v-if="currentRegisterType"
              v-model="availableColumns"
              :defaults="defaultColumns"
              :withColumnsOrder="true"
              :storageKey="`tags-register-${currentRegisterType.toLowerCase()}`" />

            <!-- Selection indicator -->
            <v-chip
              v-if="hasSelectedRows"
              small
              color="primary"
              close
              class="ml-3 pl-4 selection-chip"
              @click:close="clearSelection">
              <v-icon left small>mdi-checkbox-marked-outline</v-icon>
              {{ selectedRowCount }} selected
            </v-chip>
          </div>

          <!-- Right Section: Isolate in 3D -->
          <div class="toolbar-right">
            <v-btn
              v-if="hasSelectedRows"
              small
              color="primary"
              outlined
              class="ml-2"
              @click="isolateSelectedIn3d">
              <v-icon left small>mdi-cube-scan</v-icon>
              Isolate in 3D ({{ selectedRowCount }})
            </v-btn>
          </div>
        </div>
      </template>

      <!-- Selection Header Checkbox -->
      <template v-slot:[`header.data-table-select`]>
        <v-simple-checkbox
          :value="allPageRowsSelected"
          :indeterminate="somePageRowsSelected"
          color="primary"
          @input="toggleSelectAllOnPage"
          :ripple="false" />
      </template>

      <!-- Selection Row Checkbox -->
      <template v-slot:[`item.data-table-select`]="{ item }">
        <v-simple-checkbox
          :value="isRowSelected(item)"
          color="primary"
          @input="toggleRowSelection(item)"
          :ripple="false" />
      </template>

      <!-- Custom header template with filter buttons ---->
      <!-- Exclude 'data-table-select' - it has its own dedicated slot above (lines 86-93) -->
      <template
        v-for="header in visibleHeaders.filter(h => h.value !== 'data-table-select')"
        v-slot:[`header.${header.value}`]="{ header: columnHeader }">
        <div :key="header.value" class="header-with-filter d-flex align-center">
          <span class="header-text" :title="columnHeader ? columnHeader.text : ''">{{ columnHeader ? columnHeader.text : '' }}</span>
          <!-- Sort arrow rendered in-line with the text (Vuetify's own arrow is hidden in CSS): it
               keeps Vuetify's class, so hover/active/desc states still come from the table's CSS. -->
          <v-icon v-if="header.sortable" small class="v-data-table-header__icon header-sort-icon">$sort</v-icon>
          <span class="header-spacer"></span>
          <v-menu
            v-if="header.value !== 'actions' && header.value !== 'name' && header.value !== 'id' && !isBooleanHeader(header.value)"
            :value="activeFilterMenu === header.value"
            @input="(val) => toggleFilterMenu(header.value, val)"
            offset-y
            :close-on-content-click="false"
            nudge-left="10">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="ml-1 filter-btn"
                :class="{ 'filter-active': hasColumnFilter(header.value) }"
                v-bind="attrs"
                v-on="on">
                <v-icon small :color="hasColumnFilter(header.value) ? 'primary' : 'grey'">
                  {{ hasColumnFilter(header.value) ?
                    (getColumnFilterType(header.value) === 'select' ? 'mdi-filter-check' : 'mdi-filter') :
                    'mdi-filter-outline' }}
                </v-icon>
                <v-badge v-if="hasColumnFilter(header.value)" dot overlap color="primary">
                </v-badge>
              </v-btn>
            </template>

            <!-- Filter menu content ---->
            <v-card class="filter-menu" width="360">
              <v-card-title class="py-2 px-3">
                <span class="text-subtitle-2">Filter {{ columnHeader ? columnHeader.text : '' }}</span>
              </v-card-title>
              <v-card-text class="pb-2">
                <!-- Filter Type Selection ---->
                <v-radio-group
                  v-model="tempFilterType"
                  row
                  dense
                  class="mt-0 mb-3"
                  hide-details
                  @change="onFilterTypeChange">
                  <v-radio label="Search" value="search" class="mr-4"></v-radio>
                  <v-radio
                    :label="`Select ${loadingFilterValues.has(header.value) ? '(Loading...)' : ''}`"
                    value="select"
                    :disabled="isBooleanHeader(header.value)">
                  </v-radio>
                </v-radio-group>

                <!-- Search Input ---->
                <v-text-field
                  v-if="tempFilterType === 'search'"
                  v-model="tempFilterValue"
                  label="Search..."
                  outlined
                  dense
                  clearable
                  hide-details
                  :disabled="tempFilterType !== 'search'"
                  @keydown.enter="applyColumnFilter(header.value)"
                  @keydown.escape="closeFilterMenu"
                  @input="onTempFilterChange">
                  <template v-slot:append>
                    <v-icon>mdi-magnify</v-icon>
                  </template>
                </v-text-field>

                <!-- Autocomplete Dropdown ---->
                <v-autocomplete
                  v-else-if="tempFilterType === 'select'"
                  v-model="tempSelectedValue"
                  :items="columnFilterValues[header.value] || []"
                  label="Type to search values..."
                  placeholder="Start typing to filter..."
                  outlined
                  dense
                  clearable
                  hide-details
                  :loading="loadingFilterValues.has(header.value)"
                  :disabled="tempFilterType !== 'select'"
                  :search-input.sync="autocompleteSearch"
                  :filter="autocompleteFilter"
                  @keydown.enter="applyColumnFilter(header.value)"
                  @keydown.escape="closeFilterMenu"
                  @input="onAutocompleteSelect">
                  <template v-slot:no-data>
                    <div class="px-4 py-2 text-center text-caption grey--text">
                      {{ loadingFilterValues.has(header.value) ? 'Loading values...' :
                         autocompleteSearch ? 'No matching values found' : 'Start typing to search...' }}
                    </div>
                  </template>
                  <template v-slot:append-item v-if="!loadingFilterValues.has(header.value) && (!columnFilterValues[header.value] || columnFilterValues[header.value].length === 0)">
                    <div class="px-4 py-2 text-center text-caption grey--text">
                      No unique values found for this column
                    </div>
                  </template>
                </v-autocomplete>
              </v-card-text>
              <v-card-actions class="px-3 py-3">
                <v-btn small text color="grey" @click="clearColumnFilter(header.value)">
                  Clear
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn small text @click="closeFilterMenu"> Cancel </v-btn>
                <v-btn small color="primary" @click="applyColumnFilter(header.value)">
                  Apply
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>
      </template>

      <!-- Name Column -->
      <template v-slot:[`item.name`]="{ item }">
        <div class="d-flex align-center">
          <!-- Harvest Status Icon -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                :color="getHarvestStatus(item).color"
                class="mr-2 harvest-status-icon"
                :class="`harvest-status-icon--${getHarvestStatus(item).key}`"
                v-bind="attrs"
                v-on="on"
              >
                {{ getHarvestStatus(item).icon }}
              </v-icon>
            </template>
            <span>{{ getHarvestStatus(item).label }}</span>
          </v-tooltip>

          <!-- Tag Name with dynamic color -->
          <span
            class="tag-name font-weight-medium"
            :class="`tag-name--${getHarvestStatus(item).key}`"
          >
            {{ item.name }}
          </span>

          <!-- Warning icon for unharvested master tags -->
          <v-tooltip bottom v-if="isUnharvestedMaster(item)">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                color="amber darken-2"
                class="ml-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-alert-circle-outline
              </v-icon>
            </template>
            <span>Not Harvested - No 3D objects linked</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Actions Column -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center">
          <!-- Edit Mode Actions -->
          <template v-if="isEditingRow(item.id)">
            <!-- Save Button with change indicator -->
            <v-btn 
              icon 
              small 
              @click.stop="saveRowEdit(item)" 
              :loading="isSavingRow(item.id)" 
              :disabled="getModifiedFieldsCount() === 0"
              :title="getModifiedFieldsCount() > 0 ? `Save ${getModifiedFieldsCount()} changes` : 'No changes to save'">
              <v-badge
                v-if="getModifiedFieldsCount() > 0"
                :content="getModifiedFieldsCount()"
                color="orange darken-1"
                overlap
                small>
                <v-icon size="20" color="success">mdi-check</v-icon>
              </v-badge>
              <v-icon v-else size="20" color="grey lighten-1">mdi-check</v-icon>
            </v-btn>
            <!-- Cancel Button -->
            <v-btn icon small @click.stop="cancelRowEdit" :title="'Cancel Changes'">
              <v-icon size="20" color="error">mdi-close</v-icon>
            </v-btn>
          </template>

          <!-- Normal Mode Actions -->
          <template v-else>
            <!-- Edit Icon -->
            <v-btn icon small v-if="$has(permissions.Tags[currentRegisterType].Update)" @click.stop="startRowEdit(item)" :title="`Edit ${item.name}`">
              <v-icon size="20" color="success">mdi-pencil</v-icon>
            </v-btn>

            <!-- Delete Icon -->
            <v-btn v-if="false" icon small @click.stop="deleteTag(item)" :title="`Delete ${item.name}`">
              <v-icon size="20" color="error">mdi-delete</v-icon>
            </v-btn>

            <!-- 3D Viewer Button - Enhanced for multi-facility master tags -->
            <v-menu
              v-if="show3dButtonVisible(item)"
              :value="showFacilityMenu && menuTargetItem?.id === item.id"
              @input="(val) => { if (!val) { showFacilityMenu = false; menuTargetItem = null; } }"
              :close-on-content-click="true"
              :z-index="999"
              offset-y
              left
              transition="scale-transition"
            >
              <template v-slot:activator="{ attrs: menuAttrs }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      icon
                      small
                      :color="get3dButtonColor(item)"
                      v-bind="{ ...tooltipAttrs, ...menuAttrs }"
                      v-on="tooltipOn"
                      @click.stop="handle3dClick(item)"
                      @contextmenu.prevent.stop="handle3dRightClick(item)"
                    >
                      <v-icon size="20">mdi-cube-outline</v-icon>
                      <span v-if="has3dContextMenu(item)" class="ds-context-menu-dot" aria-hidden="true"></span>
                    </v-btn>
                  </template>
                  <span>{{ get3dTooltip(item) }}</span>
                </v-tooltip>
              </template>

              <!-- Facility Selection Menu -->
              <v-card class="facility-menu" elevation="8">
                <v-list dense class="facility-menu__list">
                  <v-list-item
                    v-if="menuTargetCanZoom"
                    class="facility-menu__item"
                    @click="zoomTagIn3d(menuTargetItem)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon small color="primary">mdi-magnify-scan</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="facility-menu__name">{{ zoom3dLabel }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider v-if="menuTargetCanZoom && menuFacilities.length"></v-divider>
                  <v-subheader v-if="menuFacilities.length" class="facility-menu__header">
                    <v-icon small class="mr-2">mdi-map-marker-multiple</v-icon>
                    Open in Facility
                  </v-subheader>
                  <v-divider v-if="menuFacilities.length"></v-divider>
                  <v-list-item
                    v-for="facility in menuFacilities"
                    :key="facility.id"
                    class="facility-menu__item"
                    @click="openFacilityInNewTab(facility.id, menuTargetItem.id)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon small color="warning">mdi-open-in-new</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="facility-menu__name">{{ facility.name }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>

            <!-- P&ID Menu - shown when tag has P&ID documents -->
            <v-menu
              v-if="item.hasPIdDocument"
              :value="showPIdMenu && pidMenuTargetItem?.id === item.id"
              @input="(val) => { if (!val) { showPIdMenu = false; pidMenuTargetItem = null; } }"
              :close-on-content-click="true"
              :z-index="999"
              offset-y
              left
              transition="scale-transition"
            >
              <template v-slot:activator="{ attrs: menuAttrs }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      icon
                      small
                      color="warning"
                      v-bind="{ ...tooltipAttrs, ...menuAttrs }"
                      v-on="tooltipOn"
                      @click.stop="handlePIdClick(item)"
                    >
                      <v-icon size="20">mdi-file-cad</v-icon>
                    </v-btn>
                  </template>
                  <span>View P&ID</span>
                </v-tooltip>
              </template>

              <!-- P&ID Selection Menu -->
              <v-card class="facility-menu" elevation="8">
                <v-list dense class="facility-menu__list">
                  <v-subheader class="facility-menu__header">
                    <v-icon small class="mr-2">mdi-file-cad-box</v-icon>
                    Select P&ID Document
                  </v-subheader>
                  <v-divider></v-divider>
                  <v-list-item
                    v-for="doc in pidMenuDocuments"
                    :key="doc.id"
                    class="facility-menu__item"
                    @click="selectPIdDocument(doc)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon small :color="isPIdOtherFacility(doc) ? 'warning' : 'primary'">
                        {{ isPIdOtherFacility(doc) ? 'mdi-open-in-new' : 'mdi-file-pdf-box' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ doc.documentNumber || doc.documentTitle || 'Untitled' }}</v-list-item-title>
                      <v-list-item-subtitle v-if="isPIdOtherFacility(doc)">
                        {{ getFacilityName(doc.assetId) }} · Opens in new tab
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
            
            <!-- Documents Button - visible when tag has documents -->
            <v-tooltip bottom v-if="item.documentsCount > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openDocumentsTab(item)">
                  <v-badge
                    :content="item.documentsCount"
                    color="orange"
                    overlap
                    small>
                    <v-icon size="20" color="primary">mdi-file-document-outline</v-icon>
                  </v-badge>
                </v-btn>
              </template>
              <span>View Documents ({{ item.documentsCount }})</span>
            </v-tooltip>

            <!-- Linked Records - the register rows this tag is linked to (AVA-493) -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openLinkedRecords(item)">
                  <v-icon size="20" color="primary">mdi-table-search</v-icon>
                </v-btn>
              </template>
              <span>Linked records</span>
            </v-tooltip>
          </template>
        </div>
      </template>

      <!-- Custom slot for register-type specific columns (standard properties) -->
      <template v-for="column in customColumns" v-slot:[`item.${column.value}`]="{ item }">
        <!-- Skip custom attribute columns - they have their own template -->
        <template v-if="!column.isCustomAttribute">
          <div
            v-if="isEditingRow(item.id)"
            class="edit-field-container"
            :key="`edit-${item.id}-${column.value}`">
            <v-text-field
              v-model="editingData[column.value]"
              dense
              outlined
              hide-details
              :class="{
                'field-modified': isFieldModified(item, column.value),
                'field-unchanged': !isFieldModified(item, column.value)
              }"
              @keydown.enter="saveRowEdit(item)"
              @keydown.escape="cancelRowEdit"
              @input="onFieldChange(column.value, $event)">
              <template v-slot:prepend-inner v-if="isFieldModified(item, column.value)">
                <v-icon small color="orange darken-1">mdi-circle-edit-outline</v-icon>
              </template>
            </v-text-field>
            <div v-if="isFieldModified(item, column.value)" class="field-change-indicator">
              <small class="text-caption grey--text">
                Changed from: <span class="original-value">{{ getOriginalValue(item, column.value) }}</span>
              </small>
            </div>
          </div>
          <span
            v-else
            class="display-field"
            :key="`display-${item.id}-${column.value}`">
            {{ item[column.value] || '-' }}
          </span>
        </template>
        <!-- Custom attribute columns -->
        <template v-else>
          <div
            v-if="isEditingRow(item.id)"
            class="edit-field-container custom-attr-edit"
            :key="`edit-custom-${item.id}-${column.value}`">
            <!-- Text input -->
            <v-text-field
              v-if="column.dataType === 'text' || !column.dataType"
              v-model="editingData[column.value]"
              dense
              outlined
              hide-details
              :class="{
                'field-modified': isFieldModified(item, column.value),
                'field-unchanged': !isFieldModified(item, column.value)
              }"
              @keydown.enter="saveRowEdit(item)"
              @keydown.escape="cancelRowEdit"
              @input="onFieldChange(column.value, $event)">
              <template v-slot:prepend-inner v-if="isFieldModified(item, column.value)">
                <v-icon small color="orange darken-1">mdi-circle-edit-outline</v-icon>
              </template>
            </v-text-field>

            <!-- Number input -->
            <v-text-field
              v-else-if="column.dataType === 'number'"
              v-model.number="editingData[column.value]"
              type="number"
              dense
              outlined
              hide-details
              :class="{
                'field-modified': isFieldModified(item, column.value),
                'field-unchanged': !isFieldModified(item, column.value)
              }"
              @keydown.enter="saveRowEdit(item)"
              @keydown.escape="cancelRowEdit"
              @input="onFieldChange(column.value, $event)">
              <template v-slot:prepend-inner v-if="isFieldModified(item, column.value)">
                <v-icon small color="orange darken-1">mdi-circle-edit-outline</v-icon>
              </template>
            </v-text-field>

            <!-- Boolean input -->
            <v-switch
              v-else-if="column.dataType === 'boolean'"
              v-model="editingData[column.value]"
              hide-details
              dense
              class="mt-0 pt-0 custom-attr-switch"
              :class="{ 'field-modified': isFieldModified(item, column.value) }"
              @change="onFieldChange(column.value, $event)"
            />

            <!-- Date input -->
            <DatePicker
              v-else-if="column.dataType === 'date'"
              :attach="false"
              v-model="editingData[column.value]"
              dense
              outlined
              hide-details
              class="custom-attr-date"
              :class="{ 'field-modified': isFieldModified(item, column.value) }"
              @input="onFieldChange(column.value, $event)"
            />

            <div v-if="isFieldModified(item, column.value)" class="field-change-indicator">
              <small class="text-caption grey--text">
                Changed from: <span class="original-value">{{ getOriginalValue(item, column.value) }}</span>
              </small>
            </div>
          </div>
          <!-- Display mode for custom attributes -->
          <span
            v-else
            class="display-field custom-attr-value"
            :class="getCustomAttrTypeClass(column.dataType)"
            :key="`display-custom-${item.id}-${column.value}`">
            {{ formatCustomAttributeValue(item, column) }}
          </span>
        </template>
      </template>

      <!-- Loading State -->
      <template v-slot:loading>
        <div class="loading-container">
          <v-progress-circular indeterminate color="primary" size="32" width="4" class="mb-4">
          </v-progress-circular>
          <p class="text-subtitle-1 font-weight-medium">Loading Register...</p>
        </div>
      </template>

      <!-- No Data State -->
      <template v-slot:no-data>
        <div class="no-data-container">
          <v-icon large color="grey lighten-1" class="mb-4"> mdi-tag-outline </v-icon>
          <p class="text-subtitle-1 font-weight-medium">No Registers Available</p>
        </div>
      </template>
    </v-data-table>

    <!-- Register rows linked to a tag (AVA-493) - opened from the row actions -->
    <linked-register-rows-dialog
      v-model="showLinkedRecordsDialog"
      :tag-id="linkedRecordsTagId"
      :facility-id="assetId"
      :tag-name="linkedRecordsTagName" />

  </v-sheet>
</template>

<script>
  import { mapGetters } from 'vuex';
  import ColumnsVisibilityControl from '@Share/components/ColumnsVisibilityControl.vue';
  import LinkedRegisterRowsDialog from '@/features/Registers/components/admin/LinkedRegisterRowsDialog.vue';
  import { $has, formatDate } from '@/utils';
  import permissions from '@Share/config/permissions';
  import TagsRegisterService from '@Tags/services/tagsRegister.service';
  import DatePicker from '@Share/components/VDatePicker';
  import { PANEL_TAB, TAG_INFO_TAB } from '@/constants/tabs';
  import { GET_ALL_DOCUMENTS } from '@/utils/attachmentsAPI';
  import HttpService from '@Utils/httpService';
  import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
  import {
    REGISTER_LOAD,
    decideRegisterOptionsLoad,
    isRequestCancelled,
    registerOptionsSnapshot,
  } from '@Tags/utils/registerLoadPolicy';
  import {
    CLICK_BEHAVIOR,
    buildOther3dFacilities,
    hasHarvestInAnyFacility,
    resolveTag3dButton,
    tag3dNavigationFields,
    TAG_3D_LABELS,
  } from '@/utils/crossFacility';

  export default {
    name: 'TagsRegisterView',
    components: {
      ColumnsVisibilityControl,
      DatePicker,
      LinkedRegisterRowsDialog,
    },
    props: {
      currentRegisterType: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        availableColumns: [],
        permissions,
        editingRowId: null,
        editingData: {},
        originalData: {},
        modifiedFields: new Set(),
        savingRows: new Set(),
        // Keep old properties for backward compatibility
        editingItem: null,
        editingField: null,
        editingValue: null,
        savingFields: new Set(),

        // Column filter properties
        activeFilterMenu: null, // Currently open filter menu column
        tempFilterValue: '', // Temporary filter value while menu is open
        tempFilterType: 'search', // 'search' or 'select'
        tempSelectedValue: null, // Temporary selected value for select filters
        autocompleteSearch: '', // Search input for autocomplete
        columnFilterValues: {}, // { columnName: [array of unique values] }
        loadingFilterValues: new Set(), // Track which columns are loading filter values
        debounceTimer: null, // Timer for debounced data loading

        // Facility menu properties (for multi-facility master tags)
        showFacilityMenu: false, // Controls facility selection menu visibility
        menuTargetItem: null, // Tag item for which the facility menu is open
        menuFacilities: [],

        // P&ID Selection Menu properties (for multi-P&ID tags)
        showPIdMenu: false, // Controls P&ID selection menu visibility
        pidMenuDocuments: [], // List of P&ID documents for menu selection
        pidMenuTargetItem: null, // Tag item for which the P&ID menu is open

        // Linked records dialog (register rows linked to a tag)
        showLinkedRecordsDialog: false,
        linkedRecordsTagId: null,
        linkedRecordsTagName: '',

        // Row selection (persists across pagination)
        selectedRowIds: new Set(),
        selectAllOnPage: false,

        // Navigation guard - prevents stale requests during register type change
        isResetting: false,
      };
    },
    created() {
      // Keep Tags panel open while on register view
      this.previousTab = this.$store.getters['getSelectedTab'];
      this.previousIsShown = this.$store.getters['getIsTabShown'];
      // Open Tags tab and show the left panel
      // Using SHOW_PANEL to bridge both legacy and new panel system
      this.$store.dispatch('SHOW_PANEL', { tabIndex: PANEL_TAB.TAGS });
      // The query the parent's first load already asks for. Not reactive on purpose: it only
      // records what the options watcher last handled (see registerLoadPolicy.js).
      this.lastOptionsSnapshot = registerOptionsSnapshot(this.options);
    },
    beforeDestroy() {
      // Restore previous side panel state when leaving register view
      // Use actions instead of direct mutations to bridge with new panel system
      if (this.previousIsShown && this.previousTab !== undefined) {
        // Previous state was visible - use SHOW_PANEL to bridge to new panel system
        this.$store.dispatch('SHOW_PANEL', { tabIndex: this.previousTab, pinned: true });
      } else if (!this.previousIsShown) {
        // Previous state was hidden - close all panels
        this.$store.dispatch('closeLeftTab');
      }

      // Clear debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Clear any active filters and close filter menus
      this.$store.commit('TAGS_REGISTER/SET_COLUMN_FILTERS', {});
      this.activeFilterMenu = null;
    },
    computed: {
      // The open 3D menu belongs to one row; it offers "Zoom to tag in 3D" when that row can.
      menuTargetCanZoom() {
        return this.register3dButton(this.menuTargetItem)?.canZoomFromMenu === true;
      },
      zoom3dLabel() {
        return TAG_3D_LABELS.ZOOM;
      },
      ...mapGetters('TAGS_REGISTER', {
        entries: 'GET_ENTRIES',
        total: 'GET_TOTAL',
        isLoading: 'GET_LOADING',
        columnConfig: 'GET_COLUMN_CONFIG',
      }),
      ...mapGetters('MASTERS', {
        schemasCache: 'GET_SCHEMAS_CACHE',
        getSchemaForBatch: 'GET_SCHEMA_FOR_BATCH',
      }),

      // Active column filters ({ column: { type, value } }). Live in the store so the parent's
      // loads (link-status toggles) send them too; written only through SET_COLUMN_FILTERS.
      columnFilters() {
        return this.$store.getters['TAGS_REGISTER/GET_COLUMN_FILTERS'];
      },

      options: {
        get() {
          return this.$store.getters['TAGS_REGISTER/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('TAGS_REGISTER/SET_OPTIONS', val);
        },
      },

      defaultColumns() {
        return this.columnConfig(this.currentRegisterType) || [];
      },

      assetId() {
        return this.$route.params.id;
      },

      visibleHeaders() {
        const visibleCols = this.availableColumns
          // Filter out null/undefined columns and hidden columns
          .filter((col) => col && col.value && !col.hidden)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((col) => ({
            text: col.text || col.value, // Fallback to value if text is missing
            value: col.value,
            sortable: col.sortable !== false,
            width: col.width || 'auto',
          }));

        // Ensure selection column is always first
        const selectColIndex = visibleCols.findIndex(col => col.value === 'data-table-select');
        if (selectColIndex > 0) {
          const [selectCol] = visibleCols.splice(selectColIndex, 1);
          visibleCols.unshift(selectCol);
        }

        return visibleCols;
      },

      customColumns() {
        return this.availableColumns.filter(
          (col) => col && col.value && !['name', 'actions', 'data-table-select', 'id'].includes(col.value),
        );
      },

      /**
       * Custom attribute columns generated from schemas of loaded tags.
       * Creates a union of all custom attribute fields across all batches.
       */
      customAttributeColumns() {
        if (!this.entries || this.entries.length === 0 || !this.schemasCache) {
          return [];
        }

        // Collect unique batch IDs from entries
        const batchIds = new Set();
        this.entries.forEach(tag => {
          if (tag.masterImportBatchId) {
            batchIds.add(tag.masterImportBatchId);
          }
        });

        if (batchIds.size === 0) {
          return [];
        }

        // Build union of all schema fields across batches
        const fieldMap = new Map(); // fieldName -> { displayName, dataType, order }
        let maxOrder = 200; // Start after standard columns

        batchIds.forEach(batchId => {
          const schema = this.getSchemaForBatch(batchId) || [];
          schema.forEach(field => {
            if (!fieldMap.has(field.name)) {
              fieldMap.set(field.name, {
                name: field.name,
                displayName: field.displayName || this.formatFieldName(field.name),
                dataType: field.dataType || 'text',
                order: maxOrder + (field.order || 0),
              });
              maxOrder++;
            }
          });
        });

        // Convert to column definitions
        return Array.from(fieldMap.values()).map(field => ({
          text: field.displayName,
          value: `customAttr_${field.name}`,
          hidden: false, // Visible by default per user's decision
          sortable: false, // Custom attributes not sortable via server
          order: field.order,
          hidable: true,
          width: 150,
          isCustomAttribute: true,
          customAttrName: field.name,
          dataType: field.dataType,
        }));
      },

      /**
       * Columns that are custom attributes (for special handling in templates)
       */
      customAttrColumnsList() {
        return this.availableColumns.filter(col => col.isCustomAttribute);
      },

      /**
       * Facility dictionary (id → name) from store.
       * Used to display facility names in the multi-facility menu.
       */
      facilityDict() {
        return this.$store.getters.getFacilityDict || {};
      },

      // Row selection computed properties
      selectedRowIdsArray() {
        return Array.from(this.selectedRowIds);
      },
      selectedRowCount() {
        return this.selectedRowIds.size;
      },
      hasSelectedRows() {
        return this.selectedRowIds.size > 0;
      },
      allPageRowsSelected() {
        if (this.entries.length === 0) return false;
        return this.entries.every(row => this.selectedRowIds.has(row.id));
      },
      somePageRowsSelected() {
        if (this.entries.length === 0) return false;
        const selectedCount = this.entries.filter(row => this.selectedRowIds.has(row.id)).length;
        return selectedCount > 0 && selectedCount < this.entries.length;
      },
    },
    methods: {
      $has,

      // ═══════════════════════════════════════════════════════════
      // Component Lifecycle & State Reset Methods
      // ═══════════════════════════════════════════════════════════

      /**
       * Resets all local component state when navigating to a different register type.
       * This ensures a clean slate for the new register, preventing stale filters,
       * selections, and editing states from persisting across navigation.
       */
      resetComponentState() {
        debugLog('TagsRegister', 'Resetting component state for new register type');

        // Set resetting flag to prevent stale requests from options watcher
        this.isResetting = true;

        // NOTE: Do NOT call CANCEL_CURRENT_REQUEST here!
        // The parent's debouncedLoadRegisterData() already cancels the old request
        // before starting the new one. If we cancel here, we'd cancel the NEW request.

        // Clear debounce timer to prevent stale data loads from THIS component
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = null;
        }

        // Reset column visibility (will be re-populated by defaultColumns watcher)
        this.availableColumns = [];

        // Reset row editing state
        this.editingRowId = null;
        this.editingData = {};
        this.originalData = {};
        this.modifiedFields.clear();
        this.savingRows.clear();

        // Reset legacy editing properties
        this.editingItem = null;
        this.editingField = null;
        this.editingValue = null;
        this.savingFields.clear();

        // Reset column filters (most important for clean navigation)
        this.$store.commit('TAGS_REGISTER/SET_COLUMN_FILTERS', {});
        this.activeFilterMenu = null;
        this.tempFilterValue = '';
        this.tempFilterType = 'search';
        this.tempSelectedValue = null;
        this.autocompleteSearch = '';
        this.columnFilterValues = {};
        this.loadingFilterValues.clear();

        // Reset facility/P&ID menus
        this.showFacilityMenu = false;
        this.menuTargetItem = null;
        this.showPIdMenu = false;
        this.pidMenuDocuments = [];
        this.pidMenuTargetItem = null;

        // Reset row selection (clear all selections when changing register type)
        this.selectedRowIds = new Set();
        this.selectAllOnPage = false;

        // Clear resetting flag after a tick to allow watchers to settle
        this.$nextTick(() => {
          this.isResetting = false;
        });
      },

      // ═══════════════════════════════════════════════════════════
      // Custom Attribute Methods
      // ═══════════════════════════════════════════════════════════

      /**
       * Format a field name to display name (camelCase/snake_case to Title Case)
       */
      formatFieldName(name) {
        return name
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/^\w/, c => c.toUpperCase())
          .trim();
      },

      /**
       * Get custom attribute value from a tag
       */
      getCustomAttributeValue(item, attrName) {
        if (!item || !item.customAttributes) return null;
        return item.customAttributes[attrName];
      },

      /**
       * Format custom attribute value based on data type
       */
      formatCustomAttributeValue(item, column) {
        const attrName = column.customAttrName;
        const value = this.getCustomAttributeValue(item, attrName);

        if (value === null || value === undefined || value === '') return '-';

        switch (column.dataType) {
          case 'date':
            return formatDate(value) || value;
          case 'boolean':
            return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
          case 'number':
            return typeof value === 'number' ? value.toLocaleString() : value;
          default:
            return String(value);
        }
      },

      /**
       * Check if a column is a custom attribute column
       */
      isCustomAttributeColumn(column) {
        return column && column.isCustomAttribute === true;
      },

      /**
       * Get column data type class for styling
       */
      getCustomAttrTypeClass(dataType) {
        return `custom-attr-${dataType || 'text'}`;
      },

      /**
       * Update available columns with custom attribute columns when entries change
       */
      updateColumnsWithCustomAttributes() {
        const baseColumns = this.columnConfig(this.currentRegisterType) || [];
        const customAttrCols = (this.customAttributeColumns || []).filter(c => c && c.value);

        // Check if custom attribute columns already exist to avoid duplicates
        const existingCustomAttrValues = new Set(
          this.availableColumns
            .filter(c => c && c.isCustomAttribute && c.value)
            .map(c => c.value)
        );

        // Only add new custom attribute columns
        const newCustomAttrCols = customAttrCols.filter(
          c => c && c.value && !existingCustomAttrValues.has(c.value)
        );

        if (newCustomAttrCols.length > 0) {
          // Merge new custom attribute columns with existing columns
          // Filter out null/undefined columns to prevent header issues
          this.availableColumns = [
            ...this.availableColumns.filter(c => c && c.value && !c.isCustomAttribute),
            ...customAttrCols
          ];
        }
      },

      // ═══════════════════════════════════════════════════════════

      startEdit(item, field, currentValue) {
        if (!this.$has(this.permissions.Tags[this.currentRegisterType]?.Update)) return;

        this.editingItem = item;
        this.editingField = field;
        this.editingValue = currentValue;

        this.$nextTick(() => {
          const editor = this.$refs[`${field}Editor`] || this.$refs[`${field}Editor`]?.[0];
          if (editor && editor.focus) {
            editor.focus();
          }
        });
      },

      // Row-level editing methods
      startRowEdit(item) {
        if (!this.$has(this.permissions.Tags[this.currentRegisterType]?.Update)) return;

        this.editingRowId = item.id;
        this.editingData = {};
        this.originalData = {};
        this.modifiedFields.clear();

        // Initialize editing data with current values and store originals
        this.customColumns.forEach(column => {
          let value;

          if (column.isCustomAttribute) {
            // For custom attributes, get value from customAttributes object
            const attrName = column.customAttrName;
            value = this.getCustomAttributeValue(item, attrName);
            // Convert null/undefined to appropriate default based on data type
            if (value === null || value === undefined) {
              value = column.dataType === 'boolean' ? false : '';
            }
          } else {
            // Standard property
            value = item[column.value] || '';
          }

          this.$set(this.editingData, column.value, value);
          this.$set(this.originalData, column.value, value);
        });
      },

      async saveRowEdit(item) {
        if (!this.editingRowId) return;

        this.savingRows.add(item.id);

        try {
          // Collect changed fields - separate standard fields from custom attributes
          const cells = [];
          const customAttributeChanges = {};

          for (const column of this.customColumns) {
            const newValue = this.editingData[column.value];
            const originalValue = this.originalData[column.value];

            // Only include fields that were actually modified
            if (newValue !== originalValue) {
              if (column.isCustomAttribute) {
                // Custom attribute change - collect separately
                customAttributeChanges[column.customAttrName] = newValue;
              } else {
                // Standard field change
                cells.push({
                  ColumnTitle: column.value,
                  Value: newValue
                });
              }
            }
          }

          const hasStandardChanges = cells.length > 0;
          const hasCustomAttrChanges = Object.keys(customAttributeChanges).length > 0;

          // Only make request if there are actual changes
          if (hasStandardChanges || hasCustomAttrChanges) {
            const changesData = {
              cells: cells,
              id: item.id
            };

            // Include custom attributes in the payload if there are changes
            if (hasCustomAttrChanges) {
              changesData.customAttributes = customAttributeChanges;
            }

            // Try to send all changes in one request, fallback to individual requests
            try {
              await this.$store.dispatch('TAGS_REGISTER/UPDATE_TAG_MULTIPLE', {
                tagId: item.id,
                changes: changesData,
                tagType: this.currentRegisterType,
                assetId: this.assetId,
              });
            } catch (error) {
              // If bulk update doesn't exist, fall back to individual requests
              if (error.message?.includes('unknown action type')) {
                const promises = [];
                for (const cell of cells) {
                  promises.push(
                    this.$store.dispatch('TAGS_REGISTER/UPDATE_TAG', {
                      tagId: item.id,
                      field: cell.ColumnTitle,
                      value: cell.Value,
                      tagType: this.currentRegisterType,
                      assetId: this.assetId,
                    })
                  );
                }
                // For custom attributes, we'd need a separate update
                // For now, include in last standard update or separate call
                await Promise.all(promises);
              } else {
                throw error;
              }
            }

            // Only update local data after successful save
            // Update standard fields in local reference
            for (const cell of cells) {
              this.$set(item, cell.ColumnTitle, cell.Value);
            }
            // Custom attributes are updated via Vuex mutation which replaces the entry
          }

          this.cancelRowEdit();
        } catch (error) {
          debugError('Failed to update tag:', error);
          this.$emit('error', error);
          // Don't update local data on error - keep original values
        } finally {
          this.savingRows.delete(item.id);
        }
      },

      cancelRowEdit() {
        this.editingRowId = null;
        this.editingData = {};
        this.originalData = {};
        this.modifiedFields.clear();
      },

      isEditingRow(itemId) {
        return this.editingRowId === itemId;
      },

      isSavingRow(itemId) {
        return this.savingRows.has(itemId);
      },

      isFieldModified(item, fieldName) {
        if (!this.isEditingRow(item.id)) return false;
        const currentValue = this.editingData[fieldName];
        const originalValue = this.originalData[fieldName];
        return currentValue !== originalValue;
      },

      getOriginalValue(item, fieldName) {
        return this.originalData[fieldName] || '-';
      },

      onFieldChange(fieldName, value) {
        // Update the modified fields set
        const originalValue = this.originalData[fieldName];
        if (value !== originalValue) {
          this.modifiedFields.add(fieldName);
        } else {
          this.modifiedFields.delete(fieldName);
        }
      },

      getModifiedFieldsCount() {
        return this.modifiedFields.size;
      },

      getRowClass(item) {
        let classes = [];
        
        // Add editing class if row is being edited
        if (this.isEditingRow(item.id)) {
          classes.push('editing-row');
        }
        
        // Add warning class for unharvested master tags (no 3D objects in any facility)
        if (this.isUnharvestedMaster(item)) {
          classes.push('warning-row');
        }
        
        return classes.join(' ');
      },

      /**
       * Checks if a master tag is unharvested (no 3D objects linked anywhere).
       *
       * The warning is reserved for a tag genuinely harvested in NO facility, so it must be
       * read from the cross-facility contract (spatialTargets) rather than from
       * `availableAssets`: the API narrows children to the current facility, which left a
       * master harvested only elsewhere wrongly flagged as unharvested.
       */
      isUnharvestedMaster(item) {
        if (!item?.isMaster) return false;
        return !hasHarvestInAnyFacility(item);
      },

      /**
       * Returns harvest status info for a tag (icon, color, label, class).
       * Matches the pattern from TagCard.vue for consistency.
       */
      getHarvestStatus(item) {
        // Non-master tags: check if has children
        if (!item.isMaster) {
          const hasChildren = item.children?.length > 0;
          if (!hasChildren) {
            return {
              key: 'unharvested',
              label: 'No 3D Objects',
              icon: 'mdi-cube-off-outline',
              color: 'grey'
            };
          }
          return {
            key: 'harvested',
            label: 'Has 3D Objects',
            icon: 'mdi-cube-outline',
            color: 'success'
          };
        }

        // Master tags: harvested anywhere, read from the cross-facility contract
        const hasAnyChildren = hasHarvestInAnyFacility(item);

        if (!hasAnyChildren) {
          return {
            key: 'unharvested',
            label: 'Unharvested',
            icon: 'mdi-link-off',
            color: 'grey'
          };
        }

        if (item.isHarvestedInCurrentFacility) {
          return {
            key: 'harvested',
            label: 'Harvested',
            icon: 'mdi-link',
            color: 'success'
          };
        }

        // Harvested in other facility only
        return {
          key: 'other-facility',
          label: 'In Other Facility',
          icon: 'mdi-map-marker-multiple',
          color: 'warning'
        };
      },

      async saveEdit(item) {
        if (!this.editingField || this.editingValue === null) return;

        this.savingFields.add(`${item.id}-${this.editingField}`);

        try {
          await this.$store.dispatch('TAGS_REGISTER/UPDATE_TAG', {
            tagId: item.id,
            field: this.editingField,
            value: this.editingValue,
            tagType: this.currentRegisterType,
            assetId: this.assetId,
          });

          // Update local item
          this.$set(item, this.editingField, this.editingValue);

          this.cancelEdit();
        } catch (error) {
          debugError('Failed to update tag:', error);
          this.$emit('error', error);
        } finally {
          this.savingFields.delete(`${item.id}-${this.editingField}`);
        }
      },

      cancelEdit() {
        this.editingItem = null;
        this.editingField = null;
        this.editingValue = null;
      },

      /**
       * Open the linked-records dialog for a tag (AVA-493)
       * The tag is set before the dialog opens - the dialog fetches on the open transition
       */
      openLinkedRecords(item) {
        this.linkedRecordsTagId = item.id;
        this.linkedRecordsTagName = item.name || '';
        this.showLinkedRecordsDialog = true;

        debugLog('Opening linked records dialog', {
          tagId: item.id,
          tagName: this.linkedRecordsTagName,
          registerType: this.currentRegisterType,
        });
      },

      /**
       * Open Tag Info Panel on Documents tab
       * Fetches fresh data first to ensure complete tag information
       */
      async openDocumentsTab(item) {
        try {
          // 1. Determine the correct view store based on tag type
          const viewStore = `${this.currentRegisterType.toUpperCase()}_VIEW`;

          // 2. Fetch fresh data from API (follows card component pattern)
          const freshTag = await this.$store.dispatch(`${viewStore}/GET_BY_ID`, { id: item.id });

          // 3. Open the Tags panel
          this.$store.dispatch('SHOW_PANEL', {
            tabIndex: PANEL_TAB.TAGS,
            pinned: true
          });

          // 4. Open the tag with Documents as default tab
          this.$store.dispatch('OPEN_TAG', {
            tag: freshTag,
            clearPrevious: true,
            defaultTab: TAG_INFO_TAB.DOCUMENTS
          });
        } catch (err) {
          debugError('Error opening tag documents tab:', err);
          this.$dialog.notify.error('Failed to load tag details', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Handle P&ID button click - opens the P&ID document or shows menu for multiple
       * Uses menu pattern (like Show in 3D) instead of dialog
       */
      async handlePIdClick(item) {
        try {
          // Get P&ID document type ID for filtering
          const pIdTypeId = this.$store.getters.GET_DOCUMENT_TYPES?.find(t => t.name === 'P&IDs')?.id;
          const currentAssetId = this.$store.state.assetId;

          // Get FacilityGroupId for cross-facility queries
          const facility = this.$store.getters['FACILITY/GET_FACILITY'];
          const facilityGroupId = facility?.facilityGroupId || null;

          // Get documents for this tag with FacilityGroup support
          const response = await HttpService.get({
            url: `/api/documents/TagId/${item.id}`,
            params: {
              AssetId: currentAssetId,
              ...(facilityGroupId && { FacilityGroupId: facilityGroupId }),
              ...(pIdTypeId && { DocumentTypeIds: [pIdTypeId] }),
            },
          });

          // Combine all P&ID sources (preferring markupsDocuments)
          const markupsDocuments = response.data?.markupsDocuments || [];
          const attachments = response.data?.attachments || [];
          const allDocs = this.deduplicatePIdDocuments(markupsDocuments, attachments);

          if (allDocs.length === 0) {
            this.$dialog.notify.warning('No P&ID document found for this tag', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          // Single document - open directly
          if (allDocs.length === 1) {
            this.pidMenuTargetItem = item;
            await this.openSinglePIdDocument(allDocs[0], item);
            return;
          }

          // Multiple documents - show menu (like Show in 3D pattern)
          this.pidMenuDocuments = allDocs;
          this.pidMenuTargetItem = item;
          this.showPIdMenu = true;
        } catch (error) {
          debugError('Error opening P&ID:', error);
          this.$dialog.notify.error('Failed to load P&ID document', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Deduplicate P&ID documents from different sources (markupsDocuments, attachments)
       * Prefers markupsDocuments over attachments when duplicates exist
       */
      deduplicatePIdDocuments(markupsDocuments, attachments) {
        const seen = new Set();
        const result = [];

        // Add markupsDocuments first (priority)
        for (const doc of markupsDocuments) {
          if (!seen.has(doc.id)) {
            seen.add(doc.id);
            result.push(doc);
          }
        }

        // Add attachments if not already present
        for (const doc of attachments) {
          if (!seen.has(doc.id)) {
            seen.add(doc.id);
            result.push(doc);
          }
        }

        return result;
      },

      /**
       * Open a single P&ID document, handling cross-facility navigation
       */
      async openSinglePIdDocument(pIdDoc, tag) {
        const currentAssetId = this.$store.state.assetId;
        const isOtherFacility = pIdDoc.assetId && pIdDoc.assetId !== currentAssetId;

        if (isOtherFacility) {
          // Cross-facility: open in new tab (like Show on 3D pattern)
          this.openPIdInNewTab(pIdDoc.assetId, tag.id, pIdDoc.id);
        } else {
          // Same facility: open in current viewer
          this.openPIdInCurrentViewer(pIdDoc, tag);
        }
      },

      /**
       * Open P&ID in current facility's viewer (existing behavior)
       * Uses OPEN_DOCUMENT_IN_VIEWER action to construct proxy URL for authenticated access
       */
      async openPIdInCurrentViewer(pIdDoc, tag) {
        // Set the tag to highlight BEFORE calling action
        this.$store.commit('setTagIdToHighlight', tag.id);

        // Extract document ID - handle different property names
        const documentId = pIdDoc.documentId || pIdDoc.id;

        if (!documentId) {
          debugWarn('TagsRegister', 'openPIdInCurrentViewer: Document ID not available - aborting');
          this.$dialog.notify.warning('Document ID not available', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        const result = await this.$store.dispatch('OPEN_DOCUMENT_IN_VIEWER', {
          document: {
            documentId: documentId,
            // Required: OPEN_DOCUMENT_IN_VIEWER signs this into the blob URL; omit it
            // and getDocumentBlobUrl returns null, so the doc silently never loads.
            // (|| url mirrors getDocumentBlobUrl's own fallback for attachment-sourced docs.)
            storageReference: pIdDoc.storageReference || pIdDoc.url,
            fileName: pIdDoc.fileName || pIdDoc.name || 'document.pdf',
            pagesMarkups: pIdDoc.pagesMarkups,
            referenceDocumentId: documentId,
          },
        });

        if (result.success) {
          this.$emit('show-viewers-section', 'Document');
        } else {
          debugWarn('TagsRegister', 'openPIdInCurrentViewer: Failed', result.error);
          this.$dialog.notify.warning(result.error || 'Failed to open document', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Open P&ID in another facility (new browser tab)
       * Follows the pattern from openFacilityInNewTab for Show on 3D
       */
      openPIdInNewTab(facilityId, tagId, documentId) {
        const route = this.$router.resolve({
          name: 'facility',
          params: { id: facilityId },
          query: {
            tagId: tagId,
            documentId: documentId, // Auto-open specific document
            openDocument: 'true', // Trigger document viewer on load
          },
        });
        window.open(route.href, '_blank');
      },

      /**
       * Handle P&ID document selection from menu
       */
      selectPIdDocument(doc) {
        if (this.isPIdOtherFacility(doc)) {
          this.openPIdInNewTab(doc.assetId, this.pidMenuTargetItem.id, doc.id);
        } else {
          this.openPIdInCurrentViewer(doc, this.pidMenuTargetItem);
        }
        this.showPIdMenu = false;
        this.pidMenuTargetItem = null;
      },

      /**
       * Check if a P&ID document is from another facility
       */
      isPIdOtherFacility(doc) {
        return doc.assetId && doc.assetId !== this.$store.state.assetId;
      },

      /**
       * Get facility name from store dictionary
       */
      getFacilityName(assetId) {
        return this.facilityDict[assetId] || 'Other Facility';
      },

      isEditing(itemId, field) {
        return this.editingItem?.id === itemId && this.editingField === field;
      },

      isSaving(itemId, field) {
        return this.savingFields.has(`${itemId}-${field}`);
      },

      update(tag) {
        this.$refs.editTag.open({
          tag: tag,
          isNew: false,
          registerType: this.currentRegisterType,
        });
      },

      editTag(tag) {
        this.update(tag);
      },

      deleteTag(tag) {
        this.$dialog.error({
          text: 'Are You Sure You Want to Delete This Tag?',
          title: 'Delete Tag',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('TAGS_REGISTER/DELETE_TAG', {
                  tagId: tag.id,
                  tagType: this.currentRegisterType,
                  assetId: this.assetId,
                });
              },
            },
          },
        });
      },

      showTagIn3d(tag) {
        this.$emit('showIn3d', tag);
      },

      // ═══════════════════════════════════════════════════════════
      // Row Selection Methods (persist across pagination)
      // ═══════════════════════════════════════════════════════════

      isRowSelected(row) {
        return this.selectedRowIds.has(row.id);
      },

      toggleRowSelection(row) {
        if (this.selectedRowIds.has(row.id)) {
          this.selectedRowIds.delete(row.id);
        } else {
          this.selectedRowIds.add(row.id);
        }
        // Reassign for Vue 2 reactivity
        this.selectedRowIds = new Set(this.selectedRowIds);
      },

      toggleSelectAllOnPage() {
        if (this.allPageRowsSelected) {
          this.entries.forEach(row => {
            this.selectedRowIds.delete(row.id);
          });
        } else {
          this.entries.forEach(row => {
            this.selectedRowIds.add(row.id);
          });
        }
        // Reassign for Vue 2 reactivity
        this.selectedRowIds = new Set(this.selectedRowIds);
      },

      clearSelection() {
        this.selectedRowIds = new Set();
      },

      /**
       * Isolate all selected tags in the 3D viewer.
       * Filters to only tags that have 3D objects, then emits to parent.
       */
      isolateSelectedIn3d() {
        const selectedTags = this.entries.filter(e => this.selectedRowIds.has(e.id));

        // Filter to tags with 3D objects
        const tagsWithObjects = selectedTags.filter(tag => {
          if (tag.isMaster) {
            return tag.isHarvestedInCurrentFacility && tag.children?.length > 0;
          }
          return tag.children?.length > 0;
        });

        if (tagsWithObjects.length === 0) {
          this.$dialog.notify.warning('None of the selected tags have 3D objects in this facility', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        const skippedCount = selectedTags.length - tagsWithObjects.length;
        if (skippedCount > 0) {
          this.$dialog.notify.info(`${skippedCount} tag(s) without 3D objects were skipped`, {
            position: 'top-right',
            timeout: 3000,
          });
        }

        this.$emit('showIn3d', tagsWithObjects);
      },

      // ═══════════════════════════════════════════════════════════
      // Multi-Facility Harvester Support Methods
      // ═══════════════════════════════════════════════════════════

      /**
       * Determines if the 3D/isolate button should be visible for a tag.
       *
       * For master tags the shared resolver decides: the button appears exactly when the
       * plan the click will execute has somewhere to go, locally or in another facility.
       * Visibility, tooltip and click all read register3dButton, so they can never disagree; a
       * master placed only from a 360 scan shows the button and frames its point.
       * For non-master: requires children (existing behavior — a manual tag lives on one
       * facility, so its narrowed children are still the whole truth).
       */
      show3dButtonVisible(item) {
        if (item.isMaster) return this.register3dButton(item).behavior !== CLICK_BEHAVIOR.NONE;
        return item.children?.length > 0;
      },

      /** The shared 3D-button decision for a master row; null for a non-master row. */
      register3dButton(item) {
        if (!item?.isMaster) return null;
        return resolveTag3dButton(item, this.assetId, { facilityDict: this.facilityDict });
      },

      /** The dot marks a right-click menu that offers more than the click does. */
      has3dContextMenu(item) {
        const decision = this.register3dButton(item);
        if (decision?.behavior !== CLICK_BEHAVIOR.LOCAL) return false;
        return decision.canZoomFromMenu || this.getOtherFacilities(item).length > 0;
      },

      /**
       * Returns button color based on harvest status.
       * Orange if tag can also be opened in other facilities, blue otherwise.
       */
      get3dButtonColor(item) {
        if (!item.isMaster) return 'primary';
        return this.getOtherFacilities(item).length > 0 ? 'warning' : 'primary';
      },

      /**
       * Other facilities this tag can be opened in, as {id, name} for the menu.
       *
       * Resolver-returned and access-filtered, the same source TagCard.vue uses:
       * `otherAvailableAssets` is derived from children the API narrowed to the current
       * facility, so it is now always empty and can no longer answer this.
       */
      getOtherFacilities(item) {
        return buildOther3dFacilities(item, this.facilityDict, this.assetId);
      },

      /**
       * Returns dynamic tooltip based on button behavior.
       *
       * Keyed on the resolver's plan, not on `isHarvestedInCurrentFacility`: the button is
       * shown whenever that plan can act, including for a master placed here but harvested
       * nowhere. Reading harvest instead would promise "Open in another facility" on a
       * click that stays in this one.
       */
      get3dTooltip(item) {
        if (!item.isMaster) return `View ${item.name} in 3D`;

        const otherFacilities = this.getOtherFacilities(item);
        const decision = this.register3dButton(item);
        const clickBehavior = decision.behavior;
        if (clickBehavior === CLICK_BEHAVIOR.LOCAL) {
          return this.has3dContextMenu(item)
            ? `${decision.localLabel} (right-click for more options)`
            : decision.localLabel;
        }
        if (clickBehavior === CLICK_BEHAVIOR.NAVIGATE_SINGLE && otherFacilities.length === 1) {
          return `Open in ${otherFacilities[0].name}`;
        }
        return 'Open in another facility';
      },

      /**
       * Handle left click on 3D button.
       * Behavior depends on harvest status.
       */
      async handle3dClick(item) {
        if (!item?.id) return;
        const decision = this.register3dButton(item);
        const outcome = await this.$store.dispatch('NAVIGATE_TAG_SPATIALLY', {
          tagId: item.id,
          intent: 'isolate-3d',
          ...(decision ? tag3dNavigationFields(decision) : { isolate: true }),
          source: 'ui',
          assetId: this.assetId,
        });
        this.apply3dNavigationChoices(item, outcome);
      },

      /** Frame the tag in 3D without isolating it - the right-click alternative to Isolate. */
      async zoomTagIn3d(item) {
        this.showFacilityMenu = false;
        const decision = this.register3dButton(item);
        if (!decision?.canZoomFromMenu) return;
        const outcome = await this.$store.dispatch('NAVIGATE_TAG_SPATIALLY', {
          tagId: item.id,
          intent: 'isolate-3d',
          ...tag3dNavigationFields(decision, { zoom: true }),
          source: 'ui',
          assetId: this.assetId,
        });
        this.apply3dNavigationChoices(item, outcome);
      },

      /**
       * Handle right click on 3D button.
       * Shows facility menu if other facilities are available.
       */
      async handle3dRightClick(item) {
        if (!item?.id) return;
        const outcome = await this.$store.dispatch('NAVIGATE_TAG_SPATIALLY', {
          tagId: item.id,
          intent: 'isolate-3d',
          resolveOnly: true,
          isolate: false,
          source: 'ui',
          assetId: this.assetId,
        });
        this.apply3dNavigationChoices(item, outcome, true);
        // The zoom item needs no remote alternative to be worth opening the menu for.
        if (this.register3dButton(item)?.canZoomFromMenu) this.showFacilityMenu = true;
      },
      apply3dNavigationChoices(item, outcome, forceMenu = false) {
        const plan = outcome?.plan;
        const alternatives = plan?.kind === 'navigate-facility' ? [plan] : (plan?.alternatives || []);
        this.menuFacilities = alternatives
          .filter((alternative) => alternative.viewer === '3d')
          .map((alternative) => ({
            id: alternative.facilityId,
            name: this.facilityDict[alternative.facilityId] || alternative.facilityId,
          }));
        this.menuTargetItem = item;
        this.showFacilityMenu = this.menuFacilities.length > 0
          && (forceMenu || outcome?.status === 'choose-facility');
      },

      /**
       * Open a facility in a new browser tab with tag context.
       * Passes tagId and isolate params for auto-isolation on load.
       */
      async openFacilityInNewTab(facilityId, tagId) {
        if (!facilityId || !tagId) return;
        const outcome = await this.$store.dispatch('NAVIGATE_TAG_SPATIALLY', {
          tagId,
          intent: 'isolate-3d',
          selectedFacilityId: facilityId,
          resolveOnly: true,
          isolate: false,
          source: 'ui',
          assetId: this.assetId,
        });
        const plan = outcome?.plan;
        if (plan?.viewer !== '3d' || !plan.facilityId) return;
        const route = this.$router.resolve({
          name: 'facility',
          params: { id: plan.facilityId },
          query: {
            tagId: tagId,
            isolate: 'true'
          }
        });
        window.open(route.href, '_blank');
      },

      // Boolean header check method
      isBooleanHeader(columnValue) {
        const col = this.availableColumns.find((c) => c.value === columnValue);
        if (!col) return false;

        // Check if column is boolean type using multiple possible indicators
        return col.dataType === 4 || // FIELD_TYPES.BOOLEAN
               col.dataType === 'boolean' || // Legacy string type
               col.type === 'boolean' ||
               (col.text && col.text.toLowerCase().includes('boolean'));
      },

      // Column filter methods
      async toggleFilterMenu(columnValue, isOpen) {
        if (isOpen) {
          this.activeFilterMenu = columnValue;
          const existingFilter = this.columnFilters[columnValue];
          this.tempFilterType = existingFilter ? existingFilter.type : 'search';
          this.tempFilterValue = existingFilter && existingFilter.type === 'search' ? existingFilter.value : '';
          this.tempSelectedValue = existingFilter && existingFilter.type === 'select' ? existingFilter.value : null;
        } else {
          this.activeFilterMenu = null;
          this.tempFilterValue = '';
          this.tempFilterType = 'search';
          this.tempSelectedValue = null;
          this.autocompleteSearch = '';
        }
      },

      closeFilterMenu() {
        this.activeFilterMenu = null;
        this.tempFilterValue = '';
        this.tempFilterType = 'search';
        this.tempSelectedValue = null;
        this.autocompleteSearch = '';
      },

      hasColumnFilter(columnValue) {
        const filter = this.columnFilters[columnValue];
        return filter && filter.value && String(filter.value).trim() !== '';
      },

      getColumnFilterType(columnValue) {
        const filter = this.columnFilters[columnValue];
        return filter ? filter.type : 'search';
      },

      getColumnFilterValue(columnValue) {
        const filter = this.columnFilters[columnValue];
        return filter ? filter.value : '';
      },

      applyColumnFilter(columnValue) {
        let filterValue = null;
        let filterType = this.tempFilterType;

        if (filterType === 'search' && this.tempFilterValue && this.tempFilterValue.trim() !== '') {
          filterValue = this.tempFilterValue.trim();
        } else if (filterType === 'select' && this.tempSelectedValue) {
          filterValue = this.tempSelectedValue;
        }

        if (!filterValue) {
          this.clearColumnFilter(columnValue);
          return;
        }
        this.$store.commit('TAGS_REGISTER/SET_COLUMN_FILTERS', {
          ...this.columnFilters,
          [columnValue]: { type: filterType, value: filterValue },
        });
        this.closeFilterMenu();
        this.reloadFromFirstPage();
      },

      clearColumnFilter(columnValue) {
        const remaining = { ...this.columnFilters };
        delete remaining[columnValue];
        this.$store.commit('TAGS_REGISTER/SET_COLUMN_FILTERS', remaining);
        this.closeFilterMenu();
        this.reloadFromFirstPage();
      },

      clearAllFilters() {
        this.$store.commit('TAGS_REGISTER/SET_COLUMN_FILTERS', {});
        this.reloadFromFirstPage();
      },

      /**
       * A changed filter starts again on page 1 (page 3 of a narrower result may not exist).
       * If the page actually moves, the options watcher loads it; otherwise load here.
       */
      reloadFromFirstPage() {
        if ((this.options.page || 1) !== 1) {
          this.$store.commit('TAGS_REGISTER/SET_OPTIONS', { page: 1 });
          return;
        }
        this.reloadDataNow();
      },

      onTempFilterChange() {
        // Optional: Add live filtering or validation here
      },

      onAutocompleteSelect(value) {
        // Called when user selects a value from autocomplete
        this.tempSelectedValue = value;
      },

      /**
       * Custom filter function for autocomplete dropdown.
       * Performs case-insensitive substring matching.
       */
      autocompleteFilter(item, queryText) {
        if (!queryText) return true;
        const itemText = String(item).toLowerCase();
        const searchText = queryText.toLowerCase();
        return itemText.includes(searchText);
      },

      async loadColumnFilterValues(columnValue) {
        if (this.loadingFilterValues.has(columnValue) || this.isBooleanHeader(columnValue)) {
          return;
        }

        this.loadingFilterValues.add(columnValue);
        try {
          // First try to get values from server API
          const response = await TagsRegisterService.getColumnFilterValues(
            this.currentRegisterType,
            this.assetId,
            columnValue
          );
          const serverValues = response.data || [];
          this.$set(this.columnFilterValues, columnValue, serverValues);
        } catch (error) {
          debugWarn(`Failed to load filter values from server for column ${columnValue}:`, error);

          // Fallback: Extract unique values from current entries
          try {
            const currentEntries = this.$store.getters['TAGS_REGISTER/GET_ENTRIES'] || [];
            const uniqueValues = [...new Set(
              currentEntries
                .map(entry => entry[columnValue])
                .filter(value => value !== null && value !== undefined && value !== '')
                .map(value => String(value).trim())
                .filter(value => value.length > 0)
            )].sort();

            this.$set(this.columnFilterValues, columnValue, uniqueValues);

            // Show a warning that filter values are limited to current page
            if (uniqueValues.length > 0) {
              debugWarn(`Using fallback filter values (limited to current page) for column: ${columnValue}`);
            }
          } catch (fallbackError) {
            debugError(`Failed to load fallback filter values for column ${columnValue}:`, fallbackError);
            this.$set(this.columnFilterValues, columnValue, []);
          }
        } finally {
          this.loadingFilterValues.delete(columnValue);
        }
      },

      async onFilterTypeChange() {
        // Clear temp values when switching filter types
        this.tempFilterValue = '';
        this.tempSelectedValue = null;
        this.autocompleteSearch = '';

        // Only load filter values when user specifically selects 'select' type
        if (this.tempFilterType === 'select' && this.activeFilterMenu) {
          const columnValue = this.activeFilterMenu;
          if (!this.isBooleanHeader(columnValue) && !this.columnFilterValues[columnValue]) {
            await this.loadColumnFilterValues(columnValue);
          }
        }
      },

      // Helper method to refresh filter values for a specific column if needed
      async refreshColumnFilterValues(columnValue) {
        if (this.columnFilterValues[columnValue] && !this.isBooleanHeader(columnValue)) {
          // Clear cached values and they will reload on next user interaction
          delete this.columnFilterValues[columnValue];
        }
      },

      /**
       * Reload for a discrete action (page, sort, page size, column filter): no debounce.
       * LOAD_TAGS aborts any request still in flight, so the newest action always wins.
       */
      reloadDataNow() {
        if (this.isResetting) {
          debugLog('TagsRegister', 'Skipping reload - component is resetting');
          return;
        }
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = null;
        }
        this.reloadData();
      },

      // Debounced reload for typed search text only (see registerLoadPolicy.js)
      debouncedReloadData() {
        // Skip if component is resetting (navigation in progress)
        if (this.isResetting) {
          debugLog('TagsRegister', 'Skipping reload - component is resetting');
          return;
        }

        // Show loader IMMEDIATELY for responsive feedback
        // User should see loading state as soon as they interact
        this.$store.commit('TAGS_REGISTER/SET_LOADING', true);

        // Cancel any in-flight request IMMEDIATELY when user triggers a new action
        // Note: We set loading=true BEFORE cancel to prevent flicker
        // (CANCEL_CURRENT_REQUEST sets loading=false, but we want it true)
        if (this.$store.state.TAGS_REGISTER?.currentRequestController) {
          this.$store.state.TAGS_REGISTER.currentRequestController.abort();
          this.$store.commit('TAGS_REGISTER/CLEAR_REQUEST_CONTROLLER');
        }

        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          // Double-check resetting flag before actual reload
          if (!this.isResetting) {
            this.reloadData();
          } else {
            // If we're resetting, turn off the loader we set
            this.$store.commit('TAGS_REGISTER/SET_LOADING', false);
          }
        }, 500);
      },

      // Reload data method
      async reloadData() {
        // Skip if component is resetting (navigation in progress)
        if (this.isResetting) {
          debugLog('TagsRegister', 'Skipping reloadData - component is resetting');
          return;
        }

        // NOTE: Cancellation is handled in two places:
        // 1. debouncedReloadData() - cancels immediately on user action
        // 2. LOAD_TAGS action - cancels any existing request before starting new one
        // We don't call CANCEL_CURRENT_REQUEST here to avoid setting loading=false
        // which would cause a brief flicker before LOAD_TAGS sets it back to true

        // LOAD_TAGS reads the table options, link status and column filters from the store.
        try {
          await this.$store.dispatch('TAGS_REGISTER/LOAD_TAGS', {
            tagType: this.currentRegisterType,
            assetId: this.assetId,
          });
        } catch (err) {
          // A newer action aborted this request; that action's load is already in flight.
          // Falling back here would abort it and reload without the column filters.
          if (isRequestCancelled(err)) return;
          debugError('Failed to reload data with filters:', err);
          // Fall back to loading without filters if there's an error
          await this.$store.dispatch('TAGS_REGISTER/LOAD_TAGS', {
            tagType: this.currentRegisterType,
            assetId: this.assetId,
            options: { columnFilters: {} }
          });
        }
      },
    },
    watch: {
      // Watch for register type changes and reset all local state
      currentRegisterType: {
        handler(newType, oldType) {
          if (oldType && newType !== oldType) {
            this.resetComponentState();
          }
        },
        immediate: false,
      },

      // Watch for options changes (pagination, sorting, search) and reload data
      // NOTE: This is the ONLY watcher for options - parent's watcher was removed to prevent duplicate requests
      options: {
        handler(newOptions) {
          // Vue 2's deep watcher hands back the same mutated object as old and new value, so
          // compare against a snapshot of the last handled query instead.
          const previous = this.lastOptionsSnapshot;
          const next = registerOptionsSnapshot(newOptions);
          this.lastOptionsSnapshot = next;

          // Skip if component is resetting (navigation between register types in progress);
          // the parent loads the new register with the current options.
          if (this.isResetting) {
            return;
          }

          const decision = decideRegisterOptionsLoad(previous, next);
          if (decision === REGISTER_LOAD.NOW) {
            this.reloadDataNow();
          } else if (decision === REGISTER_LOAD.DEBOUNCE) {
            this.debouncedReloadData();
          }
        },
        deep: true,
      },

      defaultColumns: {
        handler(newColumns) {
          if (newColumns && newColumns.length > 0) {
            this.availableColumns = [...newColumns];
          }
        },
        immediate: true,
      },

      // Watch entries to update custom attribute columns when data loads
      entries: {
        handler(newEntries) {
          if (newEntries && newEntries.length > 0) {
            this.$nextTick(() => {
              this.updateColumnsWithCustomAttributes();
            });
          }
        },
        immediate: false,
      },

      // Watch custom attribute columns to ensure they're merged when schemas load
      customAttributeColumns: {
        handler(newCols) {
          if (newCols && newCols.length > 0) {
            this.updateColumnsWithCustomAttributes();
          }
        },
        immediate: false,
      },
    },
  };
</script>

<style lang="scss" scoped>
  .register-view-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
    position: relative; // Required for absolute overlay positioning
  }

  // Loading Overlay Styles
  .register-loading-overlay {
    border-radius: 4px;

    :deep(.v-overlay__content) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .tag-name {
    font-weight: 600;
    transition: color 0.2s ease;

    // Default - primary color
    color: var(--v-primary-base);

    // Harvested - Confident teal/emerald tone
    &--harvested {
      color: #0d9488;
    }

    // Other Facility - Warm amber attention
    &--other-facility {
      color: #f59e0b;
    }

    // Unharvested - Subdued gray
    &--unharvested {
      color: #6b7280;
    }
  }

  // Harvest Status Icon Styles
  .harvest-status-icon {
    transition: transform 0.2s ease, color 0.2s ease;

    &:hover {
      transform: scale(1.15);
    }

    // Harvested - Teal with subtle glow
    &--harvested {
      color: #0d9488 !important;

      &:hover {
        color: #0f766e !important;
      }
    }

    // Other Facility - Amber warning
    &--other-facility {
      color: #f59e0b !important;

      &:hover {
        color: #d97706 !important;
      }
    }

    // Unharvested - Muted gray
    &--unharvested {
      color: #9ca3af !important;
      opacity: 0.8;

      &:hover {
        color: #6b7280 !important;
        opacity: 1;
      }
    }
  }

  // Enhanced table toolbar (matching RegisterTable style)
  .table-toolbar-enhanced {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: var(--v-bg2-base);
    border-bottom: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12));
    gap: 16px;
    flex-wrap: wrap;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .selection-chip {
    font-weight: 500;
  }

  .v-simple-checkbox {
    width: 100%;
    display: flex;
    text-align: center;
    justify-content: center;
    align-items: center;
    height: -webkit-fill-available;
  }

  // Selection column sizing
  :deep(th:first-child),
  :deep(td:first-child) {
    padding-left: 8px !important;
    padding-right: 0 !important;
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
  }

  .register-table {
    flex: 1;
    background-color: white;
    margin-right: 16px;
    margin-bottom: 16px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden !important;

    ::v-deep .v-data-table__wrapper > table tr td {
      border: 1px solid #e6e6e608;
    }

    ::v-deep .v-data-table__wrapper {
      overflow-x: auto;
      border-radius: 0px !important;
    }

    ::v-deep .v-data-table-header {
      background-color: #f4f5f7;
      th {
        font-weight: 600;
        color: #172b4d;
        white-space: nowrap;
      }
    }
  }

  .loading-container,
  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
    color: #6b778c;
  }

  /* Enhanced table styling */
  // Compact single-line header: 6px + 32px content + 6px ≈ 44px (fits the 28px filter button).
  :deep(.v-data-table-header th) {
    border-bottom: 2px solid #e0e0e0 !important;
    vertical-align: middle !important;
    height: auto !important;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }

  :deep(.v-data-table tbody tr:nth-child(even)) {
    background-color: rgba(0, 0, 0, 0.02);
  }

  :deep(.v-data-table tbody tr:hover) {
    background-color: rgba(33, 150, 243, 0.08) !important;
  }

  :deep(.v-data-table-header) {
    position: sticky !important;
    top: 0 !important;
    z-index: 2 !important;
    background: var(--v-bg1-base) !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  }

  :deep(.register-table) {
    flex: 1;
    margin: 12px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden !important;
  }

  :deep(.register-table .v-data-table__wrapper > table tr td) {
    // padding: 0 !important;
    border: 1px solid #e6e6e688 !important;
  }

  :deep(.register-table .v-data-table-header th) {
    border: 1px solid #e6e6e638 !important;
  }

  .display-field {
    padding: 8px;
    border-radius: 4px;
    min-height: 20px;
    display: inline-block;
    width: 100%;
  }

  .edit-field-container {
    position: relative;
    min-height: 60px;
    padding: 4px 0;
  }

  .field-modified {
    :deep(.v-input__control) {
      .v-input__slot {
        border-color: #ff9800 !important;
        background-color: rgba(255, 152, 0, 0.05) !important;
        transition: all 0.3s ease;
      }
    }

    :deep(.v-text-field__details) {
      display: none !important;
    }
  }

  .field-unchanged {
    :deep(.v-input__control) {
      .v-input__slot {
        background-color: rgba(0, 0, 0, 0.02);
        transition: all 0.3s ease;
      }
    }

    :deep(.v-text-field__details) {
      display: none !important;
    }
  }

  .field-change-indicator {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 2px 8px;
    margin-top: -20px;
    
    .original-value {
      font-weight: 500;
      color: rgba(0, 0, 0, 0.6);
      font-style: italic;
    }
  }

  .edit-actions {
    display: flex;
    gap: 4px;
    margin-left: 8px;
  }

  /* Enhanced table row styling during edit mode */
  :deep(.v-data-table tbody tr) {
    &.editing-row {
      background-color: rgba(33, 150, 243, 0.02) !important;
      border-left: 4px solid #2196F3;
      
      td {
        padding-top: 12px !important;
        padding-bottom: 12px !important;
        vertical-align: top !important;
      }
    }

    &.warning-row {
      background-color: rgba(255, 193, 7, 0.04) !important;
      border-left: 3px solid #FFC107;
      position: relative;
      
      &:hover {
        background-color: rgba(255, 193, 7, 0.06) !important;
      }

      td {
        position: relative;
        
        &:first-child::before {
          content: '';
          position: absolute;
          left: -3px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(180deg, #FFC107 0%, #FF9800 100%);
        }
      }
    }

    &.editing-row.warning-row {
      background-color: rgba(255, 193, 7, 0.05) !important;
      border-left: 4px solid #2196F3;
      box-shadow: inset 3px 0 0 #FFC107;
    }
  }

  /* Animation for field changes */
  .field-modified {
    animation: field-highlight 0.5s ease-in-out;
  }

  @keyframes field-highlight {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.02);
    }
    100% {
      transform: scale(1);
    }
  }


  // Column filter styles
  .header-with-filter {
    min-height: 32px;
    flex-wrap: nowrap;

    // One line per header: long titles truncate (full text in the title tooltip).
    .header-text {
      flex: 0 1 auto;
      min-width: 0;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-sort-icon {
      flex: 0 0 auto;
      margin-left: 4px;
    }

    // Pushes the filter button to the right edge of the column.
    .header-spacer {
      flex: 1 1 auto;
    }
  }

  // The sort arrow lives inside .header-with-filter; Vuetify's own arrow (a direct child of th)
  // would otherwise wrap onto a second line under the full-width header.
  :deep(.register-table .v-data-table-header th > .v-data-table-header__icon) {
    display: none;
  }

  .header-with-filter {

    .filter-btn {
      opacity: 0.6;
      transition: all 0.2s ease;
      margin-left: 4px;

      &:hover {
        opacity: 1;
        background-color: rgba(33, 150, 243, 0.04);
      }

      &.filter-active {
        opacity: 1;
        background-color: rgba(33, 150, 243, 0.08);

        .v-icon {
          color: #1976d2 !important;
        }
      }
    }
  }

  .filter-menu {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15) !important;
    border-radius: 8px;
    overflow: hidden;

    .v-card__title {
      background-color: var(--v-bg1-base);
      border-bottom: 1px solid var(--v-bg1-base);

      .text-subtitle-2 {
        font-weight: 600;
        color: var(--v-text-primary);
      }
    }

    .v-card__text {
      padding: 16px !important;

      .v-radio-group {
        .v-input--radio-group__input {
          margin-top: 0;
        }

        .v-radio {
          margin-right: 12px;

          :deep(.v-label) {
            font-size: 0.875rem;
            font-weight: 500;
          }
        }
      }
    }

    .v-card__actions {
      border-top: 1px solid var(--v-bg1-base);
      background-color: var(--v-bg1-base);
    }
  }

  // Header styling enhancements
  :deep(.v-data-table-header th) {
    padding-left: 8px !important;
    padding-right: 8px !important;

    .header-with-filter {
      width: 100%;
      justify-content: space-between;
    }
  }

  // Filter button hover states
  :deep(.v-btn--icon.filter-btn) {
    width: 28px !important;
    height: 28px !important;

    .v-icon {
      font-size: 16px !important;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Facility Menu Styles (Multi-Facility Harvester Support)
  // ═══════════════════════════════════════════════════════════
  .facility-menu {
    min-width: 200px;
    border-radius: 8px;
    overflow: hidden;

    &__list {
      padding: 0;
    }

    &__header {
      font-size: 0.75rem;
      font-weight: 600;
      color: #666;
      padding: 8px 12px;
      background: #f5f5f5;
    }

    &__item {
      transition: background-color 0.15s ease;

      &:hover {
        background-color: rgba(255, 152, 0, 0.08);
      }
    }

    &__name {
      font-size: 0.875rem;
      font-weight: 500;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Custom Attribute Styles
  // ═══════════════════════════════════════════════════════════

  // Custom attribute value display
  .custom-attr-value {
    font-size: 0.85rem;

    &.custom-attr-number {
      font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
      font-variant-numeric: tabular-nums;
    }

    &.custom-attr-date {
      color: var(--v-golden-base, #d2a000);
    }

    &.custom-attr-boolean {
      font-weight: 500;
    }
  }

  // Custom attribute edit controls
  .custom-attr-edit {
    .custom-attr-switch {
      margin-top: 4px;

      &.field-modified {
        :deep(.v-input--switch__track) {
          border: 2px solid #fb8c00;
        }
      }
    }

    .custom-attr-date {
      &.field-modified {
        :deep(.v-input__slot) {
          border-color: #fb8c00 !important;
        }
      }
    }
  }

  // Header styling for custom attribute columns (purple accent)
  :deep(.v-data-table-header th) {
    &[aria-label*="customAttr_"] {
      .header-text {
        color: var(--v-purple-base, #665B9B);
        font-weight: 600;
      }

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 4px;
        bottom: 4px;
        width: 3px;
        background-color: var(--v-purple-base, #665B9B);
        border-radius: 2px;
      }
    }
  }
</style>
